import type { DsiSourceInfo } from './live-preview';
import type { SiteSourceContent } from './site-games';

/**
 * What to call each page of the brand's site in the Games tab: the words the site's own navigation shows for
 * it, in the owner's language, and a name read from its path where the navigation has none. The site pages
 * come from the service by path only; a player knows them by the navigation's words. Pure helpers only.
 */

/** The data types a site's navigation is made of, the category bar's first: its words are the ones a player
 *  reads for a page. Then the top navigation, the side menu, the footer's link columns. */
const NAVIGATION_TYPES = [
  'general-resource-data-element',
  'spark-top-navigation.slot-component',
  'side-menu.link',
  'provider-menu.link',
];

/**
 * The navigation sources among a page's sources, in the order their words are preferred. `exclude` leaves out
 * the category pages' own link lists: the first link of one is the category page itself, labelled "Home".
 */
export const navigationSourceIds = (sources: DsiSourceInfo[], exclude: ReadonlySet<string>): string[] =>
  NAVIGATION_TYPES.flatMap((type) => sources.filter((s) => s.typeId === type && !exclude.has(s.id)).map((s) => s.id));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nonEmpty = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);

/** A link's text in `language`: a plain text as is, else that language, English, then any translation. */
const textIn = (text: unknown, language: string): string | null => {
  if (!isRecord(text)) return nonEmpty(text);
  return (
    nonEmpty(text[language]) ??
    nonEmpty(text.en) ??
    Object.values(text)
      .map(nonEmpty)
      .find((value): value is string => value !== null) ??
    null
  );
};

/** The path a link goes to, without its query or a trailing slash, or null for anything that is not one. */
const pathOf = (link: unknown): string | null => {
  const text = nonEmpty(link);
  if (!text) return null;
  try {
    const path = new URL(text, 'https://site.invalid').pathname.replace(/\/+$/, '');
    return path || '/';
  } catch {
    return null;
  }
};

/** One navigation link: the path it goes to and its text in the language asked for. */
export type NavLink = { path: string; text: string };

/** Every link in the contents that has a path and a text, in `language`, in the order the contents give them. */
export const navLinks = (contents: SiteSourceContent[], language: string): NavLink[] => {
  const out: NavLink[] = [];
  for (const content of contents) {
    for (const row of content.data) {
      if (!isRecord(row)) continue;
      const props = isRecord(row.properties) ? row.properties : {};
      const path = pathOf(props.url ?? props.href ?? row.url ?? row.href);
      const text = textIn(props.text ?? row.text, language);
      if (path && text) out.push({ path, text });
    }
  }
  return out;
};

/** Every path the navigation links to, wherever a row holds the link (a `url` or `href`, at any depth: a footer
 *  column nests its links, a top bar its slots), with a text or without. What says a page is reachable on the site. */
export const linkedPaths = (contents: SiteSourceContent[]): Set<string> => {
  const out = new Set<string>();
  const visit = (value: unknown, depth: number): void => {
    if (depth > 8) return;
    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1);
      return;
    }
    if (!isRecord(value)) return;
    for (const [key, item] of Object.entries(value)) {
      const path = key === 'url' || key === 'href' ? pathOf(item) : null;
      if (path) out.add(path);
      else visit(item, depth + 1);
    }
  };
  for (const content of contents) visit(content.data, 0);
  return out;
};

/** What the site's navigation calls each path it links to, in `language`. The first link to a path with a
 *  text wins, in the order the contents are given. */
export const navigationLabels = (contents: SiteSourceContent[], language: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const { path, text } of navLinks(contents, language)) if (!(path in out)) out[path] = text;
  return out;
};

/** A text map (`{ en: 'Slots', es: 'Tragamonedas' }`) in `language`, else English; null when neither is there.
 *  How the service names a site page. */
export const labelIn = (label: Record<string, string> | undefined, language: string): string | null =>
  nonEmpty(label?.[language]) ?? nonEmpty(label?.en) ?? null;

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * What the site's navigation calls a game list, from the links that open it: a category page links each of its
 * lists at `<page>/<segment>`, where the segment is the list's source id (`/slots/spk-top-games`) or its tail
 * (`/jackpots/egt_egypt_quest` opens `jackpot_games_egt_egypt_quest`). A link whose last segment IS the id wins;
 * else the longest segment the id ends with after a `_` or `-`. `exclude` leaves out the links to the section
 * pages themselves (a category's first link is "Home", to the category page). Null when no link opens the list.
 */
export const listNavLabel = (listId: string, links: NavLink[], exclude: ReadonlySet<string>): string | null =>
  listNavMatch(listId, links, exclude)?.text ?? null;

/** The link that opens a list (see `listNavLabel`) and where it sits in `links`; null when none does. */
const listNavMatch = (
  listId: string,
  links: NavLink[],
  exclude: ReadonlySet<string>,
): { text: string; index: number } | null => {
  const id = listId.toLowerCase();
  let best: { text: string; index: number; score: number } | null = null;
  links.forEach(({ path, text }, index) => {
    if (exclude.has(path)) return;
    const segment = decodeSegment(path.split('/').filter(Boolean).pop() ?? '').toLowerCase();
    if (!segment) return;
    const score =
      id === segment
        ? Number.MAX_SAFE_INTEGER
        : id.endsWith(`_${segment}`) || id.endsWith(`-${segment}`)
          ? segment.length
          : 0;
    if (score > 0 && (!best || score > best.score)) best = { text, index, score };
  });
  return best;
};

/**
 * Lists in the order the navigation links them (the links' order, as `listNavLabel` matches them), the lists no
 * link opens after, by id. A stable order, whatever order the lists were reported in.
 */
export const inNavOrder = <T extends { id: string }>(
  lists: T[],
  links: NavLink[],
  exclude: ReadonlySet<string>,
): T[] => {
  const at = new Map(lists.map((list) => [list.id, listNavMatch(list.id, links, exclude)?.index ?? links.length]));
  return [...lists].sort(
    (a, b) =>
      (at.get(a.id) ?? links.length) - (at.get(b.id) ?? links.length) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
};

/** A site menu often shouts its labels ("LIVE CASINO"); as a tab or chip name that reads as "Live Casino".
 *  Mixed-case labels are the site's own casing and are kept. */
export const titleCaseShouting = (label: string): string =>
  label === label.toUpperCase() && label !== label.toLowerCase()
    ? label.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_m: string, sp: string, ch: string) => sp + ch.toUpperCase())
    : label;

/** A name read from a path, for a page the navigation does not name: its last segment in words
 *  ("/live-casino" is "Live casino"). */
export const pathLabel = (path: string): string => {
  const last = path.split('/').filter(Boolean).pop() ?? '';
  const words = last.replace(/[-_]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : path;
};
